import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiStar,
  FiMessageSquare,
  FiThumbsUp,
  FiFilter,
  FiChevronDown,
  FiTrendingUp,
  FiCalendar,
  FiAward,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiMessageCircle,
  FiZap,
  FiTarget,
  FiHeart,
} from "react-icons/fi";
import "./ReviewsTab.css";

// ============================================
// Animation Variants
// ============================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const cardHover = {
  y: -4,
  transition: { duration: 0.2 },
};

// ============================================
// Sub-Components
// ============================================

// Star Rating Display
const StarRating = ({ rating, size = "medium", showValue = false }) => {
  const stars = Array.from({ length: 5 }, (_, i) => i < Math.floor(rating));
  const hasHalf = rating % 1 >= 0.5;

  return (
    <div className={`rev-stars rev-stars--${size}`}>
      {stars.map((filled, i) => (
        <FiStar
          key={i}
          className={`rev-star ${filled ? "rev-star--filled" : ""} ${
            !filled && i === Math.floor(rating) && hasHalf
              ? "rev-star--half"
              : ""
          }`}
        />
      ))}
      {showValue && (
        <span className="rev-stars__value">{rating.toFixed(1)}</span>
      )}
    </div>
  );
};

// Circular Progress for "Would Collaborate Again"
const CircularProgress = ({ percentage, label }) => {
  const circumference = 2 * Math.PI * 36;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="rev-circular">
      <svg className="rev-circular__svg" viewBox="0 0 80 80">
        <circle
          className="rev-circular__track"
          cx="40"
          cy="40"
          r="36"
          strokeWidth="6"
          fill="none"
        />
        <motion.circle
          className="rev-circular__progress"
          cx="40"
          cy="40"
          r="36"
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
          style={{
            strokeDasharray: circumference,
            transform: "rotate(-90deg)",
            transformOrigin: "center",
          }}
        />
      </svg>
      <div className="rev-circular__content">
        <span className="rev-circular__value">{percentage}%</span>
        <span className="rev-circular__label">{label}</span>
      </div>
    </div>
  );
};

// Rating Distribution Bar
const RatingBar = ({ rating, count, total, delay = 0 }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="rev-dist__row">
      <div className="rev-dist__stars">
        <span className="rev-dist__num">{rating}</span>
        <FiStar className="rev-dist__icon" />
      </div>
      <div className="rev-dist__bar">
        <motion.div
          className="rev-dist__fill"
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.6, delay: delay * 0.1, ease: "easeOut" }}
        />
      </div>
      <span className="rev-dist__count">{count}</span>
    </div>
  );
};

// Category Progress Bar
const CategoryBar = ({ label, value, color, icon: Icon }) => {
  const percentage = (value / 5) * 100;

  return (
    <div className="rev-cat__item">
      <div className="rev-cat__header">
        <div className="rev-cat__label">
          <Icon className="rev-cat__icon" style={{ color }} />
          <span>{label}</span>
        </div>
        <span className="rev-cat__value" style={{ color }}>
          {value.toFixed(1)}
        </span>
      </div>
      <div className="rev-cat__bar">
        <motion.div
          className="rev-cat__fill"
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ background: color }}
        />
      </div>
    </div>
  );
};

// Hero Stats Card
const StatsHeroCard = ({ reviews, reviewStats }) => {
  const stats = useMemo(() => {
    // Only use reviewStats if it has actual meaningful data
    const hasValidStats = reviewStats && 
      ((reviewStats.totalRatings > 0) || 
       (reviewStats.totalReviews > 0) || 
       (reviewStats.averageRating > 0));

    if (hasValidStats) {
      const distribution = reviewStats.distribution || {};
      return {
        averageRating: reviewStats.averageRating || 0,
        totalReviews:
          reviewStats.totalRatings || reviewStats.totalReviews || 0,
        distribution: [5, 4, 3, 2, 1].map(
          (star) => distribution[star.toString()] || distribution[star] || 0
        ),
        categoryAverages: reviewStats.categoryAverages || {},
        wouldCollaborateAgainPercentage:
          reviewStats.wouldCollaborateAgainPercentage,
        recommendationBreakdown: reviewStats.recommendationBreakdown || {},
      };
    }

    if (!reviews?.length) {
      return {
        averageRating: 0,
        totalReviews: 0,
        distribution: [0, 0, 0, 0, 0],
        categoryAverages: {},
        wouldCollaborateAgainPercentage: null,
        recommendationBreakdown: {},
      };
    }

    const total = reviews.length;
    // FIX: Support both overallRating and rating fields
    const sum = reviews.reduce(
      (acc, r) => acc + (r.overallRating || r.rating || 0),
      0
    );
    const avg = total > 0 ? sum / total : 0;

    const distribution = [5, 4, 3, 2, 1].map(
      (star) =>
        reviews.filter((r) => (r.overallRating || r.rating) === star).length
    );

    // Calculate would collaborate again percentage from reviews
    const collaborateAgainReviews = reviews.filter(
      (r) => r.wouldCollaborateAgain !== undefined
    );
    const wouldCollaborate = collaborateAgainReviews.filter(
      (r) => r.wouldCollaborateAgain === true
    ).length;
    const wouldCollaborateAgainPercentage =
      collaborateAgainReviews.length > 0
        ? Math.round((wouldCollaborate / collaborateAgainReviews.length) * 100)
        : null;

    return {
      averageRating: avg,
      totalReviews: total,
      distribution,
      categoryAverages: {},
      wouldCollaborateAgainPercentage,
    };
  }, [reviews, reviewStats]);

  const categoryColors = {
    communication: "#3b82f6", // Blue
    reliability: "#10b981", // Green
    expertise: "#f59e0b", // Amber
    collaboration: "#06b6d4", // Cyan
  };

  const categoryIcons = {
    communication: FiMessageCircle,
    reliability: FiCheckCircle,
    expertise: FiAward,
    collaboration: FiUsers,
  };

  const hasCategories =
    stats.categoryAverages &&
    Object.keys(stats.categoryAverages).length > 0;

  return (
    <motion.div className="rev-hero" variants={itemVariants}>
      {/* Main Stats Section */}
      <div className="rev-hero__main">
        {/* Large Rating Display */}
        <div className="rev-hero__rating">
          <motion.span
            className="rev-hero__number"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            {typeof stats.averageRating === "number"
              ? stats.averageRating.toFixed(1)
              : stats.averageRating}
          </motion.span>
          <StarRating rating={stats.averageRating} size="large" />
          <span className="rev-hero__total">
            Based on {stats.totalReviews} review
            {stats.totalReviews !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Rating Distribution */}
        <div className="rev-hero__distribution">
          {[5, 4, 3, 2, 1].map((rating, i) => (
            <RatingBar
              key={rating}
              rating={rating}
              count={stats.distribution?.[i] || 0}
              total={stats.totalReviews}
              delay={i}
            />
          ))}
        </div>

        {/* Would Collaborate Again */}
        {stats.wouldCollaborateAgainPercentage !== null &&
          stats.wouldCollaborateAgainPercentage !== undefined && (
            <div className="rev-hero__collab">
              <CircularProgress
                percentage={stats.wouldCollaborateAgainPercentage}
                label="Would collaborate again"
              />
            </div>
          )}
      </div>

      {/* Category Breakdown */}
      {hasCategories && (
        <div className="rev-hero__categories">
          <h4 className="rev-hero__cat-title">
            <FiTarget className="rev-hero__cat-icon" />
            Category Breakdown
          </h4>
          <div className="rev-cat__grid">
            {Object.entries(stats.categoryAverages).map(([key, value]) => (
              <CategoryBar
                key={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={value}
                color={categoryColors[key] || "#64748b"}
                icon={categoryIcons[key] || FiStar}
              />
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Filter/Sort Options
const filterOptions = [
  { id: "all", label: "All Reviews", icon: FiMessageSquare },
  { id: "5", label: "5 Stars", icon: FiStar },
  { id: "4", label: "4 Stars", icon: FiStar },
  { id: "3", label: "3 Stars", icon: FiStar },
  { id: "2", label: "2 Stars", icon: FiStar },
  { id: "1", label: "1 Star", icon: FiStar },
];

const sortOptions = [
  { id: "recent", label: "Most Recent", icon: FiCalendar },
  { id: "helpful", label: "Most Helpful", icon: FiThumbsUp },
  { id: "highest", label: "Highest Rated", icon: FiTrendingUp },
  { id: "lowest", label: "Lowest Rated", icon: FiChevronDown },
];

// Toolbar Component
const ReviewsToolbar = ({
  totalCount,
  filter,
  setFilter,
  sort,
  setSort,
  showFilterDropdown,
  setShowFilterDropdown,
  showSortDropdown,
  setShowSortDropdown,
}) => {
  const currentFilter = filterOptions.find((f) => f.id === filter);
  const currentSort = sortOptions.find((s) => s.id === sort);

  return (
    <motion.div className="rev-toolbar" variants={itemVariants}>
      <div className="rev-toolbar__title">
        <FiStar className="rev-toolbar__icon" />
        <h3>Reviews & Feedback</h3>
        <span className="rev-toolbar__count">{totalCount}</span>
      </div>

      <div className="rev-toolbar__actions">
        {/* Filter Dropdown */}
        <div className="rev-dropdown">
          <motion.button
            className="rev-dropdown__trigger"
            onClick={() => {
              setShowFilterDropdown(!showFilterDropdown);
              setShowSortDropdown(false);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiFilter />
            <span>{currentFilter?.label}</span>
            <FiChevronDown
              className={`rev-dropdown__arrow ${
                showFilterDropdown ? "rev-dropdown__arrow--open" : ""
              }`}
            />
          </motion.button>

          <AnimatePresence>
            {showFilterDropdown && (
              <motion.div
                className="rev-dropdown__menu"
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.95 }}
                transition={{ duration: 0.15 }}
              >
                {filterOptions.map((option) => (
                  <button
                    key={option.id}
                    className={`rev-dropdown__item ${
                      filter === option.id ? "rev-dropdown__item--active" : ""
                    }`}
                    onClick={() => {
                      setFilter(option.id);
                      setShowFilterDropdown(false);
                    }}
                  >
                    <option.icon />
                    {option.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sort Dropdown */}
        <div className="rev-dropdown">
          <motion.button
            className="rev-dropdown__trigger"
            onClick={() => {
              setShowSortDropdown(!showSortDropdown);
              setShowFilterDropdown(false);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiTrendingUp />
            <span>{currentSort?.label}</span>
            <FiChevronDown
              className={`rev-dropdown__arrow ${
                showSortDropdown ? "rev-dropdown__arrow--open" : ""
              }`}
            />
          </motion.button>

          <AnimatePresence>
            {showSortDropdown && (
              <motion.div
                className="rev-dropdown__menu"
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.95 }}
                transition={{ duration: 0.15 }}
              >
                {sortOptions.map((option) => (
                  <button
                    key={option.id}
                    className={`rev-dropdown__item ${
                      sort === option.id ? "rev-dropdown__item--active" : ""
                    }`}
                    onClick={() => {
                      setSort(option.id);
                      setShowSortDropdown(false);
                    }}
                  >
                    <option.icon />
                    {option.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

// Review Card Component
const ReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false);

  // Backend compatibility: support multiple field names
  const reviewText = review.feedback || review.review || review.comment || "";
  const shouldTruncate = reviewText.length > 200;
  const reviewer = review.rater || review.fromUser || {};
  const overallRating = review.overallRating || review.rating || 0;
  const categoryRatings = review.ratings || {};

  const formatDate = (date) => {
    if (!date) return "Recently";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getRecommendationStyle = (level) => {
    switch (level) {
      case "highly_recommend":
        return { bg: "#dcfce7", text: "#15803d", icon: "👍" };
      case "recommend":
        return { bg: "#dbeafe", text: "#1d4ed8", icon: "👍" };
      case "neutral":
        return { bg: "#fef3c7", text: "#b45309", icon: "😐" };
      default:
        return { bg: "#fee2e2", text: "#dc2626", icon: "👎" };
    }
  };

  return (
    <motion.div
      className="rev-card"
      variants={itemVariants}
      whileHover={cardHover}
    >
      {/* Card Header */}
      <div className="rev-card__header">
        <div className="rev-card__reviewer">
          <img
            src={
              reviewer.profilePhotoUrl ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                reviewer.name || "User"
              )}&background=3b82f6&color=ffffff&size=48`
            }
            alt={reviewer.name}
            className="rev-card__avatar"
          />
          <div className="rev-card__info">
            <span className="rev-card__name">
              {review.isAnonymous ? "Anonymous Reviewer" : reviewer.name || "Anonymous"}
            </span>
            <span className="rev-card__role">
              {reviewer.professionalRole || reviewer.field || "Collaborator"}
            </span>
          </div>
        </div>

        <div className="rev-card__meta">
          <div className="rev-card__rating">
            <StarRating rating={overallRating} size="small" />
            <span className="rev-card__rating-num">{overallRating}/5</span>
          </div>
          <span className="rev-card__date">
            <FiCalendar />
            {formatDate(review.createdAt)}
          </span>
        </div>
      </div>

      {/* Category Ratings Mini */}
      {Object.keys(categoryRatings).length > 0 && (
        <div className="rev-card__categories">
          {categoryRatings.communication && (
            <span className="rev-card__cat">
              <FiMessageCircle /> {categoryRatings.communication}/5
            </span>
          )}
          {categoryRatings.reliability && (
            <span className="rev-card__cat">
              <FiCheckCircle /> {categoryRatings.reliability}/5
            </span>
          )}
          {categoryRatings.expertise && (
            <span className="rev-card__cat">
              <FiAward /> {categoryRatings.expertise}/5
            </span>
          )}
          {categoryRatings.collaboration && (
            <span className="rev-card__cat">
              <FiUsers /> {categoryRatings.collaboration}/5
            </span>
          )}
        </div>
      )}

      {/* Review Text */}
      {reviewText && (
        <div className="rev-card__content">
          <p className="rev-card__text">
            {shouldTruncate && !expanded
              ? `"${reviewText.substring(0, 200)}..."`
              : `"${reviewText}"`}
          </p>
          {shouldTruncate && (
            <button
              className="rev-card__expand"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Show less" : "Read more"}
            </button>
          )}
        </div>
      )}

      {/* Skills Highlighted */}
      {review.skillsHighlighted?.length > 0 && (
        <div className="rev-card__skills">
          <span className="rev-card__skills-label">
            <FiZap /> Skills:
          </span>
          <div className="rev-card__skills-list">
            {review.skillsHighlighted.map((skill, i) => (
              <span key={i} className="rev-card__skill">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="rev-card__footer">
        <div className="rev-card__badges">
          {/* Recommendation Level */}
          {review.recommendationLevel && (
            <span
              className="rev-card__badge"
              style={{
                background: getRecommendationStyle(review.recommendationLevel).bg,
                color: getRecommendationStyle(review.recommendationLevel).text,
              }}
            >
              {getRecommendationStyle(review.recommendationLevel).icon}{" "}
              {review.recommendationLevel === "highly_recommend"
                ? "Highly Recommended"
                : review.recommendationLevel === "recommend"
                ? "Recommended"
                : review.recommendationLevel === "neutral"
                ? "Neutral"
                : "Not Recommended"}
            </span>
          )}

          {/* Would Collaborate Again */}
          {review.wouldCollaborateAgain !== undefined && (
            <span
              className={`rev-card__badge ${
                review.wouldCollaborateAgain
                  ? "rev-card__badge--positive"
                  : "rev-card__badge--negative"
              }`}
            >
              {review.wouldCollaborateAgain ? (
                <>
                  <FiCheckCircle /> Would collaborate again
                </>
              ) : (
                <>
                  <FiXCircle /> Would not collaborate again
                </>
              )}
            </span>
          )}
        </div>

        {/* Helpful Button */}
        <motion.button
          className="rev-card__helpful"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FiThumbsUp />
          <span>Helpful ({review.helpfulCount || 0})</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

// Empty State Component
const EmptyState = ({ isOwnProfile }) => (
  <motion.div className="rev-empty" variants={itemVariants}>
    <div className="rev-empty__icon">
      <FiMessageSquare />
    </div>
    <h3 className="rev-empty__title">No reviews yet</h3>
    <p className="rev-empty__text">
      {isOwnProfile
        ? "When collaborators rate your work, their feedback will appear here."
        : "This researcher hasn't received any reviews yet."}
    </p>
    <div className="rev-empty__hint">
      <FiHeart />
      <span>Great collaborations lead to great reviews!</span>
    </div>
  </motion.div>
);

// ============================================
// Main Component
// ============================================
const ReviewsTab = ({ reviews = [], reviewStats, isOwnProfile }) => {
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Filter and sort reviews
  const filteredReviews = useMemo(() => {
    let result = [...reviews];

    // Apply filter - FIX: Support both overallRating and rating
    if (filter !== "all") {
      const rating = parseInt(filter);
      result = result.filter(
        (r) => (r.overallRating || r.rating) === rating
      );
    }

    // Apply sort - FIX: Support both overallRating and rating
    switch (sort) {
      case "helpful":
        result.sort((a, b) => (b.helpfulCount || 0) - (a.helpfulCount || 0));
        break;
      case "highest":
        result.sort(
          (a, b) =>
            (b.overallRating || b.rating || 0) -
            (a.overallRating || a.rating || 0)
        );
        break;
      case "lowest":
        result.sort(
          (a, b) =>
            (a.overallRating || a.rating || 0) -
            (b.overallRating || b.rating || 0)
        );
        break;
      case "recent":
      default:
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return result;
  }, [reviews, filter, sort]);

  // Close dropdowns when clicking outside
  const handleContainerClick = (e) => {
    if (!e.target.closest(".rev-dropdown")) {
      setShowFilterDropdown(false);
      setShowSortDropdown(false);
    }
  };

  return (
    <motion.div
      className="rev-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      onClick={handleContainerClick}
    >
      {/* Hero Stats Card - Always show if there are reviews */}
      {reviews.length > 0 && (
        <StatsHeroCard reviews={reviews} reviewStats={reviewStats} />
      )}

      {/* Toolbar */}
      <ReviewsToolbar
        totalCount={reviews.length}
        filter={filter}
        setFilter={setFilter}
        sort={sort}
        setSort={setSort}
        showFilterDropdown={showFilterDropdown}
        setShowFilterDropdown={setShowFilterDropdown}
        showSortDropdown={showSortDropdown}
        setShowSortDropdown={setShowSortDropdown}
      />

      {/* Reviews List */}
      <div className="rev-list">
        {filteredReviews.length === 0 ? (
          <EmptyState isOwnProfile={isOwnProfile} />
        ) : (
          filteredReviews.map((review, index) => (
            <ReviewCard key={review._id || index} review={review} />
          ))
        )}
      </div>
    </motion.div>
  );
};

export default ReviewsTab;
